import { AxiosResponse } from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { useFileUpload } from "@/hooks/storage";
import {
  CreateStudyQuestionRequest,
  UpdateStudyQuestionRequest,
} from "@/types/requests";
import { Question } from "@/types/resources";
import {
  CreateStudyQuestionResponse,
  GetStudyQuestionsResponse,
  UpdateStudyQuestionResponse,
} from "@/types/responses";

const useGetQuestions = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient(`/studies/${studyId}/questions`);

  const fetchQuestions = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await client.get<GetStudyQuestionsResponse>("");
      return response.data.questions;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    error,
    fetchQuestions,
    loading,
  };
};

const useCreateQuestion = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient(`/studies/${studyId}/questions`);
  const { uploadFile } = useFileUpload();

  const createQuestion = useCallback(
    async (
      data: Omit<CreateStudyQuestionRequest, "media" | "section_id"> & {
        media: ImagePickerAsset[];
        section_id?: string | null;
      }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const fileCount = data.media.length;
        const fileProgresses: number[] = new Array(fileCount).fill(0);

        const mediaPaths: string[] = await Promise.all(
          data.media.map(async (item, index) => {
            return uploadFile({
              file: item,
              folder: "studies/questions",
              resourceId: studyId,
              onProgress: (progress) => {
                fileProgresses[index] = progress;
                const totalProgress =
                  fileCount > 0
                    ? fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount
                    : 0;
                setUploadProgress(totalProgress);
              },
            });
          })
        );

        const response = await client.post<
          CreateStudyQuestionResponse,
          AxiosResponse<CreateStudyQuestionResponse>,
          CreateStudyQuestionRequest
        >("", {
          assignment: data.assignment ?? null,
          carry_forward: data.carry_forward ?? null,
          lines: data.lines.map((line) => line.trim()),
          media: mediaPaths,
          options: data.options.map((opt) => opt.trim()),
          order: data.order,
          section_id: data.section_id ?? null,
          text: data.text.trim(),
          type: data.type,
        });
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, studyId, uploadFile]
  );

  return {
    createQuestion,
    error,
    loading,
    uploadProgress,
  };
};

const useUpdateQuestion = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient(`/studies/${studyId}/questions`);
  const { uploadFile } = useFileUpload();

  const updateQuestion = useCallback(
    async (
      questionId: string,
      data: Omit<UpdateStudyQuestionRequest, "media"> & {
        media: (ImagePickerAsset | string)[];
      }
    ): Promise<Question | null> => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const newFiles = data.media.filter((item) => typeof item !== "string");
        const fileCount = newFiles.length;
        const fileProgresses: number[] = new Array(fileCount).fill(0);
        let newFileIndex = 0;

        const mediaPaths: string[] = await Promise.all(
          data.media.map(async (item) => {
            if (typeof item === "string") {
              return item;
            }
            const currentIndex = newFileIndex++;
            return uploadFile({
              file: item,
              folder: "studies/questions",
              resourceId: studyId,
              onProgress: (progress) => {
                fileProgresses[currentIndex] = progress;
                const totalProgress =
                  fileCount > 0
                    ? fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount
                    : 0;
                setUploadProgress(totalProgress);
              },
            });
          })
        );

        const response = await client.put<
          UpdateStudyQuestionResponse,
          AxiosResponse<UpdateStudyQuestionResponse>,
          UpdateStudyQuestionRequest
        >(`/${questionId}`, {
          assignment: data.assignment ?? null,
          carry_forward: data.carry_forward ?? null,
          lines: data.lines.map((line) => line.trim()),
          media: mediaPaths,
          options: data.options.map((opt) => opt.trim()),
          order: data.order,
          rules: data.rules,
          section_id: data.section_id,
          text: data.text.trim(),
        });
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, uploadFile, studyId]
  );

  return {
    error,
    loading,
    updateQuestion,
    uploadProgress,
  };
};

const useDeleteQuestion = (studyId: string, questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(`/studies/${studyId}/questions/${questionId}`);

  const deleteQuestion = useCallback(async (): Promise<boolean> => {
    setError(null);
    setLoading(true);

    try {
      await client.delete("");
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    deleteQuestion,
    error,
    loading,
  };
};

export {
  useCreateQuestion,
  useDeleteQuestion,
  useGetQuestions,
  useUpdateQuestion,
};
